/* istanbul ignore file */
import { useState, useMemo } from 'react'
import classname from 'classnames/bind'
import {
  Text,
  ExpandableCard,
  Button,
  hooks as adcHooks,
  constants,
} from '@adc/ui-adc'
import styles from './styles.scss'

const cx = classname.bind(styles)
const { ColorNames } = constants

interface FAQData {
  title: string
  description: string
}

const FaqList = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const isMobile = adcHooks.useIsMobileHook()
  const heightHints = useMemo(() => {
    if (isMobile) {
      return {
        collapsed: 64,
        expanded: 300,
      }
    }
    return {
      collapsed: 64,
      expanded: 200,
    }
  }, [isMobile])

  const faqData: FAQData[] = [
    {
      title: 'Will this affect my credit?',
      description:
        'No, not with Auction.com’s SmartSale program. We do not pull your credit report as part of the listing process.',
    },
    {
      title: "Can I still live in the home while it's listed?",
      description:
        'Yes. While the home is listed, you can continue to live in the home. You may request rentback as part of the listing process after the home is closed. If a foreclosure date has been scheduled, please coordinate with your listing agent on how foreclosure may impact your sale.',
    },
    {
      title: 'Is this only for foreclosures?',
      description:
        'Auction.com’s SmartSale program is designed for any type of homeowners. However, homeowners facing foreclosure have found SmartSale program particularly beneficial to receive fast and fair offers.',
    },
    {
      title: 'What information do I need to provide?',
      description:
        'You and your listing agent will have access to a listing portal to complete the listing process. Some examples of information we require are: photos, number of beds and baths, a listing description, selling strategy through auction or offer. A dedicated Auction.com concierge team will be available to provide support and guidance.',
    },
    {
      title: 'How quickly can I see buyer demand?',
      description:
        'You can see buyer demand in terms of number of views, saves and how many active bids or offers have been placed on your property in real-time, through the same listing portal provided by Auction.com. If the listing and pricing fits our buyer’s buybox, we typically see first bid or offer within 48 hours after a property is listed.',
    },
    {
      title: 'What are my selling options?',
      description:
        'Auction.com can sell your property through two options: Offer and Auction. Offers are submitted and compete based on multiple terms including price, contingencies, closing times, deposits etc. They are submitted over an extended period of time (typically weeks). Through Auctions, bids are submitted and compete based solely on price. They are submitted over a shorter period of time (typically 14 days).',
    },
    {
      title: 'Are there any upfront costs?',
      description: 'There is no upfront costs to the homeowners.',
    },
    {
      title: 'How is this different from a traditional realtor?',
      description:
        'When you list with Auction.com’s SmartSale program, you have the benefit of a local real estate agent, and the largest investor marketplace in the country.',
    },
  ]

  return (
    <div className={cx('faq-section-wrapper')}>
      <div className={cx('header-section')}>
        <Text.Header level={1} color={ColorNames.GrayDark}>
          Frequently Asked Questions
        </Text.Header>
        <Text color={ColorNames.Gray} size='small' weight='normal'>
          Get answers to common questions about claiming your listing.
        </Text>
      </div>

      <div className={cx('faq-container')}>
        {faqData.map((faq, index) => (
          <div className={cx('faq-card')} key={index}>
            <ExpandableCard
              id={index}
              heightHints={heightHints}
              active={activeIndex === index}
              onClick={id =>
                setActiveIndex(activeIndex === id ? null : (id as number))
              }
              fullPageExpandOnMobile={false}
              slots={{
                header: () => (
                  <div className={cx('faq-header')}>
                    <div className={cx('faq-title')}>
                      <Text
                        style={{ lineHeight: '24px', fontWeight: '500' }}
                        size='small'
                        color={ColorNames.GrayDark}
                        content={faq.title}
                      />
                    </div>
                  </div>
                ),
                content: () => (
                  <div className={cx('faq-content')}>
                    <Text
                      color={ColorNames.Gray}
                      size='small'
                      weight='normal'
                      style={{ lineHeight: '26px' }}
                      content={faq.description}
                    />
                  </div>
                ),
              }}
            />
          </div>
        ))}
      </div>

      <div className={cx('contact-section')}>
        <Text
          style={{ lineHeight: '24px' }}
          size='large'
          weight='normal'
          color={ColorNames.Gray}
          content='Still have questions?'
        />
        <div className={cx('phone-container')}>
          <Button
            variant='secondary'
            color={ColorNames.Action}
            onClick={() => window.open('tel:+18555550123', '_self')}
          >
            <Text
              size='small'
              weight='normal'
              color={ColorNames.Action}
              content='Call (855) 555-0123'
            />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default FaqList
